import type { ApolloError } from "@apollo/client";

import { InstitutionTool, LtiService } from "@/generated/graphql";

export enum LtiToolFamilyCode {
  Canvas = "canvas",
  Moodle = "moodle",
  Blackboard = "blackboard",
  Brightspace = "desire2learn",
}

/** Find the AGS V2 LTI service. */
export function findLTIAdvantageAGS(services: LtiService[]): LtiService | null {
  return (
    services.find(
      (service) => service.version === "v2" && service.name === "ags"
    ) ?? null
  );
}

/**  find the AGS V2 LTI service from Canvas LMS. */
export function findCanvasLTIAdvantageAGS(
  services: LtiService[]
): LtiService | null {
  return services.find(isCanvasLTIAdvantageAGS) ?? null;
}

/** Check if the given LTI AGS is from the Canvas LMS and is LTI Advantage compatible. */
export function isCanvasLTIAdvantageAGS(service: LtiService): boolean {
  return (
    service.version === "v2" &&
    service.name === "ags" &&
    (service.issuer === "https://canvas.instructure.com" ||
      service.issuer === "https://canvas.test.instructure.com" ||
      service.issuer === "https://canvas.beta.instructure.com")
  );
}

/**  find the NRPS V2 LTI service from Brightspace LMS. */
export function findBrightspaceLTIAdvantageNRPS(
  services: LtiService[]
): LtiService | null {
  return services.find(isBrightspaceLTIAdvantageNRPS) ?? null;
}

/** Check if the given LTI NRPS is from the Brightspace LMS and is LTI Advantage compatible. */
export function isBrightspaceLTIAdvantageNRPS(service: LtiService): boolean {
  return (
    service.version === "v2" &&
    service.name === "nrps" &&
    service.metaUrl !== null &&
    !!service.metaUrl.includes("/d2l/")
  );
}

/** Check if the LMS the institution uses is Canvas. */
export function isCanvasLMS(tool: InstitutionTool): boolean {
  return tool.familyCode === LtiToolFamilyCode.Canvas;
}

export function isMoodleLMS(tool: InstitutionTool): boolean {
  return tool.familyCode === LtiToolFamilyCode.Moodle;
}

/** Check if the LMS the institution uses is Brightspace. */
export function isBrightspaceLMS(tool: InstitutionTool): boolean {
  return tool.familyCode === LtiToolFamilyCode.Brightspace;
}

export function hasMoodlePluginAgs(services: LtiService[]): boolean {
  return services.some(
    (service) => service.name === "ags" && service.isLtiPluginAgs === true
  );
}

/** Returns true if the LMS extension sync action should be available for this assessment. */
export function canSyncExtensionsFromLms(
  tool: InstitutionTool,
  services: LtiService[]
): boolean {
  return (
    (isMoodleLMS(tool) && hasMoodlePluginAgs(services)) || isCanvasLMS(tool)
  );
}

export function isValidationError(error: ApolloError) {
  return (
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors[0] &&
    // in case of validation error pantheon returns formatted error with code field
    "code" in error.graphQLErrors[0] &&
    error.graphQLErrors[0].code === "VALIDATION_ERROR"
  );
}
